import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from '@heroicons/react/24/outline';

import { BillingGroupName, useBilling } from 'services/repositories/billing/billing';
import { SlideOver, SlideOverContentSkeleton } from 'components/Display/SlideOver/SlideOver';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';

import { renderModuleBreakdown } from './breakdowns/renderModuleBreakdown';
import { UsageHistoryChart } from './UsageHistoryChart';

const _PERIOD_OPTIONS_MONTHS = [1, 3, 6, 12] as const;
type PeriodMonths = (typeof _PERIOD_OPTIONS_MONTHS)[number];

type ModuleDetailSlideOverProps = {
  groupName: BillingGroupName | null;
  onClose: () => void;
  initialMonths?: PeriodMonths;
};

export const ModuleDetailSlideOver = ({ groupName, onClose, initialMonths = 6 }: ModuleDetailSlideOverProps) => {
  const { t } = useTranslation();
  const [months, setMonths] = useState<PeriodMonths>(initialMonths);

  const isOpen = groupName !== null;
  const wasOpenRef = useRef(isOpen);
  const lastGroupNameRef = useRef(groupName);
  if (!wasOpenRef.current && isOpen && months !== initialMonths) {
    setMonths(initialMonths);
  }
  wasOpenRef.current = isOpen;
  if (groupName) {
    lastGroupNameRef.current = groupName;
  }
  const displayedGroupName = groupName ?? lastGroupNameRef.current;

  const { data: billing, isLoading } = useBilling({ variables: { months } });
  const group = billing?.usage.find((g) => g.group_name === displayedGroupName) ?? null;

  const periodOptions = [
    { value: 1 as PeriodMonths, label: t('settings:usageAndModules.period.last1Month') },
    { value: 3 as PeriodMonths, label: t('settings:usageAndModules.period.last3Months') },
    { value: 6 as PeriodMonths, label: t('settings:usageAndModules.period.last6Months') },
    { value: 12 as PeriodMonths, label: t('settings:usageAndModules.period.last12Months') },
  ];

  const title = group?.display_name ?? '';

  return (
    <SlideOver open={isOpen} setOpen={onClose} title={title} size="large">
      {isLoading ? (
        <SlideOverContentSkeleton />
      ) : group ? (
        <div className="flex flex-col gap-4">
          <Dropdown<PeriodMonths>
            value={months}
            onChange={(value) => value && setMonths(value)}
            options={periodOptions}
            leftIcon={<CalendarIcon />}
          />

          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
            <span className="text-sm font-medium text-gray-900">
              {t('settings:usageAndModules.detail.creditsUsage')}
            </span>
            <span className="text-2xl font-bold text-gray-900">{Math.round(group.credits_used)}</span>
          </div>

          <UsageHistoryChart history={group.history} />

          {renderModuleBreakdown(group)}
        </div>
      ) : null}
    </SlideOver>
  );
};
